<script setup>
  import {
    computed,
    nextTick,
    onMounted,
    ref,
    watch,
  } from 'vue'

  import FeaturedBadges from './FeatureBadges.vue'

  import { useI18n } from '@shared/i18n.js'
  import { hasCityParam } from '@shared/util.js'
  import { useBookmarkProperty } from '@composables/useBookmarkProperty.js'
  import { useSwiper } from '@composables/useSwiper.js'

  const props = defineProps({
    action: {
      default: true,
      type: Boolean,
    },
    idCustom: String,
    maxImagesCount: {
      default: 25,
      type: Number
    },
    property: Object,
    selectable: {
      default: true,
      type: Boolean,
    },
  })

  const emits = defineEmits(['bookmark', 'check', 'mouseleave', 'mouseover', 'unbookmark', 'uncheck'])

  const locale = document.querySelector('html').getAttribute('lang')

  const { t } = useI18n()
  const { bookmark, bookmarkedSet, unbookmark } = useBookmarkProperty()
  const { swiperPhotoSlider } = useSwiper()

  const bookmarked = ref(false)

  const badges = computed(() => {
    const reserved = [props.property.featured, props.property.construction_status, props.property.purpose, props.property.yield].filter((v) => v).length
    const count = Math.max(0, 4 - reserved)
    const selected = new Set([...props.property.badges].sort(() => Math.random() - 0.5).slice(0, count))
    return props.property.badges.filter((b) => selected.has(b))
  })
  const id = computed(() => props.idCustom || `property-${props.property.code}`)
  const images = computed(() => props.property.images && props.property.images.length > 0 ? props.property.images.slice(0, props.maxImagesCount) : [])
  const propertyPath = computed(() => hasCityParam() ? `${props.property.path}?c` : props.property.path)
  const reservedBadgesCount = computed(() => [props.property.featured, props.property.construction_status, props.property.purpose, props.property.yield].filter((v) => v).length)

  let sliding = false
  let submitting = false

  onMounted(() => nextTick(() => {
    if (props.action) {
      const selector = `#${id.value} .photo_list`
      const swiper = swiperPhotoSlider(selector)
      swiper.on('slideChangeTransitionStart', () => sliding = true)
      swiper.on('slideChangeTransitionEnd', () => sliding = false)
    }

    setBookmarked()
  }))

  watch(bookmarkedSet, () => setBookmarked())

  /**
   * お気に入りをクリック
   */
  const handleClickBookmark = async () => {
    if (submitting) return

    submitting = true
    try {
      if (bookmarked.value) {
        await unbookmark(props.property.code)
        emits('bookmark', false)
      }
      else {
        await bookmark(props.property.code)
        emits('bookmark', true)
      }
      bookmarked.value = !bookmarked.value
    }
    finally {
      submitting = false
    }
  }

  /**
   * チェックボックスをクリック
   */
  const handleClickCheck = () => emits('check')

  /**
   * 全体をクリック。画面遷移
   */
  const handleClickProperty = () => {
    if (!sliding) {
      window.location.href = propertyPath.value
    }
  }

  /**
   * お気に入りの設定（保存値から）
   */
  const setBookmarked = () => bookmarked.value = bookmarkedSet.value.has(props.property.code)
</script>

<template>
  <div :id="id" class="item" @mouseover="emits('mouseover')" @mouseleave="emits('mouseleave')">
    <a :href="propertyPath" tabindex="-1" @click.prevent.stop="handleClickProperty">
      <div class="photo_label">
        <div class="photo_list swiper">
          <template v-if="images.length > 0">
            <div class="swiper-wrapper">
              <div v-for="(image, index) in images" :key="`property-image-${property.code}-${index}`" class="photo swiper-slide">
                <img :src="image">
              </div>
            </div>
            <div class="swiper-button-prev" @click.prevent></div>
            <div class="swiper-button-next" @click.prevent></div>
          </template>
          <template v-else>
            <img src="/images/noimage.gif" class="no-image" />
          </template>
        </div>
        <p class="place"><span>{{ property.prefecture }} {{property.property_type_for_type }}</span></p>
      </div>
      <ul class="status">
        <li v-if="property.featured" class="st01">{{ t('c.featured') }}</li>
        <li v-if="property.construction_status" class="st02">{{ property.construction_status }}</li>
        <li v-if="property.purpose" class="st03">{{ property.purpose }}</li>
        <li v-if="property.yield" class="st03-y">{{ property.yield }}</li>
        <FeaturedBadges :badges="property.badges" :reserved-count="reservedBadgesCount" />
      </ul>
      <div class="info">
        <div class="info_head">
          <p class="price">{{ property.price }}</p>
          <ul class="icon_list">
            <li v-if="selectable" :class="{ active: property.selected }" class="list_check" @click.stop.prevent="handleClickCheck">
              <span><i class="fa-solid fa-check"></i></span>
            </li>
            <li v-if="action" :class="{ active: bookmarked }" class="list_fav" @click.stop.prevent="handleClickBookmark">
              <span><i class="fa-solid fa-heart"></i></span>
            </li>
          </ul>
        </div>
        <h2 class="ttl">{{ property.name }}</h2>
        <div class="detail">
          <template v-if="locale == 'ja'">
            <p v-if="property.property_type != 'land'" class="icons_info bed" v-html="property.rooms_layout"></p>
          </template>
          <template v-else>
            <p v-if="property.property_type != 'land'" class="icons_info bed" v-html="property.beds"></p>
            <p v-if="property.property_type != 'land'" class="icons_info bath" v-html="property.bathrooms"></p>
          </template>
          <p class="icons_info size" v-html="`${property.size}${property.size_sqft}`"></p>
        </div>
        <p class="location marker">{{ property.address }}</p>
        <p class="location access">
          {{ property.station1 }}<br v-if="property.station2">{{ property.station2 }}
        </p>
      </div>
    </a>
  </div>
</template>
